import { FC } from "react";
import { Alert, Button, Modal, styled } from "@monolith-forensics/monolith-ui";
import { ArrowUpDownIcon, CheckSquareIcon, ListTreeIcon } from "lucide-react";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";

export const NESTED_VIEW_INFO_KEY = "evidence:table:nested-intro-seen";

// Reading and writing the "seen" flag are both best-effort: localStorage can be
// readable but reject writes (private browsing, quota, blocked site data), and
// it can throw on read outright. Neither failure is worth breaking the table
// for — a failed read just shows the explainer again, and a failed write means
// it shows again next session.
export const hasSeenNestedViewInfo = () => {
  try {
    return localStorage.getItem(NESTED_VIEW_INFO_KEY) === "true";
  } catch (error) {
    console.error("error", error);
    return false;
  }
};

export const markNestedViewInfoSeen = () => {
  try {
    localStorage.setItem(NESTED_VIEW_INFO_KEY, "true");
  } catch (error) {
    console.error("error", error);
  }
};

const StyledIntro = styled.div`
  font-size: 13px;
  color: ${(props) => props.theme.palette.text.secondary};
  margin-top: 4px;
`;

// Alert lays its children out with `display: flex` (row), so the body needs a
// single column-flex child or the paragraphs render side by side.
const StyledAlertBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  line-height: 1.45;
`;

const StyledRules = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const StyledRule = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 13px;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: ${(props) => props.theme.palette.text.secondary};
  }
`;

const StyledRuleTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
`;

const StyledRuleBody = styled.div`
  color: ${(props) => props.theme.palette.text.secondary};
  line-height: 1.45;
`;

const StyledFootnote = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.palette.text.secondary};
  border-top: 1px solid ${(props) => props.theme.palette.divider};
  padding-top: 12px;
`;

interface NestedViewInfoModalProps {
  open: boolean;
  onClose: () => void;
}

export const NestedViewInfoModal: FC<NestedViewInfoModalProps> = ({
  open,
  onClose,
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      showCloseButton={true}
      style={{ maxWidth: "560px", width: "100%" }}
    >
      <Modal.Title>Nested View</Modal.Title>
      <StyledIntro>
        Nested view lists parent items only. Expand a parent to see the child
        items it contains.
      </StyledIntro>

      <div style={{ margin: "20px 0px" }}>
        <Alert variant="warning" title="Search and filters match parent items">
          <StyledAlertBody>
            <div>
              Child items are shown for reference — they are not searched or
              filtered. If a search term or filter matches only a child item,
              neither that child nor its parent appears in the results.
            </div>
            <div>
              To search and filter across every item, turn nested view off. The
              table then lists parent and child items as individual rows and
              matches against all of them.
            </div>
          </StyledAlertBody>
        </Alert>
      </div>

      <StyledRules>
        <StyledRule>
          <ArrowUpDownIcon size={15} />
          <div>
            <StyledRuleTitle>Sorting orders parent items</StyledRuleTitle>
            <StyledRuleBody>
              Child items keep their existing order within a parent and are not
              re-sorted by the selected column.
            </StyledRuleBody>
          </div>
        </StyledRule>

        <StyledRule>
          <CheckSquareIcon size={15} />
          <div>
            <StyledRuleTitle>Only parent items are selectable</StyledRuleTitle>
            <StyledRuleBody>
              Child items have no checkbox in nested view. Select a parent to
              act on it.
            </StyledRuleBody>
          </div>
        </StyledRule>

        <StyledRule>
          <ListTreeIcon size={15} />
          <div>
            <StyledRuleTitle>
              Collapsed parents show a child count
            </StyledRuleTitle>
            <StyledRuleBody>
              The badge on a collapsed row is the total number of child items
              that parent contains.
            </StyledRuleBody>
          </div>
        </StyledRule>
      </StyledRules>

      <div style={{ marginTop: 20 }}>
        <StyledFootnote>
          You can reopen this from the info button next to the nested view
          toggle.
        </StyledFootnote>
      </div>

      <div style={{ marginTop: 20 }}>
        <ButtonMenu>
          <Button
            variant="contained"
            color="primary"
            size="xs"
            onClick={onClose}
          >
            Got it
          </Button>
        </ButtonMenu>
      </div>
    </Modal>
  );
};
